import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Form, Input, Select, Upload } from 'antd'
import { CheckCircleOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { ApiRequest } from '../../../services/api/api'
import { urlKey } from '../../../services/api/api-urls'
import { baseUrl } from '../../../services/api/base-url'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import moment from 'moment'
import type { UploadFile } from 'antd/es/upload/interface'
import { getAuthHeaders } from '../../../services/lib/utils/auth.utils'

interface MaintenanceLogDialogProps {
  onDialogClose: (success: boolean) => void
  selectedMachine?: any
}

const MaintenanceLogDialog = (props: MaintenanceLogDialogProps) => {
  const { selectedMachine } = props
  const [form] = Form.useForm()
  const [machineOptions, setMachineOptions] = useState<{ label: string; value: string; name: string }[]>([])
  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    form.setFieldsValue({ logDate: moment(), machineNumber: selectedMachine?.machineId ?? undefined })

    ApiRequest({
      url: 'Machine/GetAllMachine',
      method: 'get',
    }).then((res: any) => {
      const machines = res?.data?.data || []
      const options = machines.map((m: any) => ({
        label: `${m.machineId}: ${m.machineName}`,
        value: m.machineId,
        name: m.machineName
      }))
      setMachineOptions(options)
    })
  }, [])

  const onConfirm = async () => {
    try {
      const values = await form.validateFields()
      const payload = {
        date: values.logDate ? values.logDate.format('YYYY-MM-DDTHH:mm:ss') : null,
        machineId: values.machineNumber,
        problemDescription: values.problemDescription,
        images: JSON.stringify(
          fileList
            .filter(f => f.status === 'done')
            .map((f, i) => ({ uid: i, url: f.url ?? f.response, stage: 'before' }))
        ),
      }

      const result = await ApiRequest({
        url: 'Maintenance/AddLog',
        method: 'post',
        data: payload,
        isShowSpinner: true,
      })

      if (result) {
        await SweetAlertService.successMessage('Submitted successfully')
        window.location.reload()
      }
    } catch (error) {
      // validation error — antd will show inline messages
    }
  }


  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <Form form={form} layout="vertical">
        <Form.Item
          name="logDate"
          label="Date"
          rules={[{ required: true, message: 'Required' }]}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="machineNumber"
          label="Machine Name"
          rules={[{ required: true, message: 'Required' }]}
        >
        <Select
          placeholder="Select machine"
          showSearch
          optionLabelProp="label"
          filterOption={(input, option) =>
            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
          }
          allowClear
        >
          {machineOptions.map(opt => (
            <Select.Option key={opt.value} value={opt.value} label={opt.name}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
        </Form.Item>

        <Form.Item
          name="problemDescription"
          label="Problem Description"
          rules={[{ required: true, message: 'Required' }]}
        >
          <Input.TextArea rows={4} showCount maxLength={500} />
        </Form.Item>
        <Form.Item
          name="photo"
          label="Upload Photos"
        >
        <Upload
          action={baseUrl + 'Common/UploadImage'}
          headers={getAuthHeaders()}
          name="imageFile"
            listType="picture"
            multiple
            fileList={fileList}
            onChange={({ fileList: newList }) => setFileList(newList)}
            itemRender={(originNode, file, _list, actions) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {originNode}
                <DeleteOutlined
                  onClick={actions.remove}
                  style={{ color: 'red', marginLeft: 8, fontSize: 16, cursor: 'pointer' }}
                />
              </div>
            )}
          >
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </Form.Item>
      </Form>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          paddingTop: '16px',
          borderTop: '1px solid #f0f0f0',
          marginTop: '8px',
        }}
      >

        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={onConfirm}
          style={{ minWidth: '90px', backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

export default MaintenanceLogDialog
